import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class CryptoService {
    private rawAesKey: ArrayBuffer | null = null;
    private aesKey: CryptoKey | null = null;

    private publicKeyBase64: string | null = null;
    private privateKey: CryptoKey | null = null;

    get hasAesKey(): boolean {
        return !!this.aesKey;
    }

    get publicKey(): string | null {
        return this.publicKeyBase64;
    }

    async setAesKey(base64: string): Promise<void> {
        if (!this.privateKey) {
            console.warn('[CryptoService] Private key is missing.');
            return;
        }

        const encryptedBytes = this.base64ToBytes(base64);

        this.rawAesKey = await crypto.subtle.decrypt(
            { name: 'RSA-OAEP' },
            this.privateKey,
            encryptedBytes
        );

        this.aesKey = await crypto.subtle.importKey(
            'raw',
            this.rawAesKey,
            { name: 'AES-CBC' },
            false,
            ['decrypt']
        );
    }

    async decrypt(base64Cipher: string): Promise<string> {
        if (!this.aesKey || !base64Cipher) {
            return base64Cipher;
        }

        try {
            const bytes = this.base64ToBytes(base64Cipher);

            const iv = bytes.slice(0, 16);
            const cipherText = bytes.slice(16);

            const decrypted = await crypto.subtle.decrypt(
                { name: 'AES-CBC', iv },
                this.aesKey,
                cipherText
            );

            return new TextDecoder().decode(decrypted);
        } catch (e) {
            return base64Cipher;
        }
    }

    async generateRsaKeyPair(): Promise<void> {
        if (this.privateKey && this.publicKeyBase64) {
            return;
        }

        const params: RsaHashedKeyGenParams = {
            name: 'RSA-OAEP',
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: 'SHA-1'
        };

        const keyPair = await crypto.subtle.generateKey(params, true, [
            'encrypt',
            'decrypt'
        ]);

        const exported = await crypto.subtle.exportKey('spki', keyPair.publicKey);

        this.publicKeyBase64 = this.bytesToBase64(new Uint8Array(exported));
        this.privateKey = keyPair.privateKey;
    }

    private base64ToBytes(base64: string): Uint8Array {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    }

    private bytesToBase64(bytes: Uint8Array): string {
        let s = '';
        for (let i = 0; i < bytes.length; i++) {
            s += String.fromCharCode(bytes[i]);
        }
        return btoa(s);
    }
}
